import { Position } from "@blueprintjs/core"
import { TimePrecision } from "@blueprintjs/datetime"
import CustomButton from "components/Atoms/CustomButton"
import CustomDatePicker from "components/Atoms/CustomDatePicker"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FormController from "components/Molecules/FormController"
import CreateTripDialog from "components/Organisms/CreateTripDialog"
import { useSummaryAllOrders } from "graphql/dispatch/dispatch.hooks"
import { USERS } from "graphql/selector.queries.gql"
import { useSearchFilter } from "hooks/searchFilterHooks"
import { useFormDisabled } from "hooks/useAuth"
import { values } from "lodash-es"
import React, { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useCurrentDc } from "store/auth.redux"
import {
  CHANGE_DISPATCH_SELECTOR,
  useSelectedOrdersArray,
} from "store/dispatch.redux"
import AppToaster from "utils/AppToaster"
import VehicleSelector from "./VehicleSelector"

const LOADING_PATTERNS = [
  {
    id: "ORD",
    name: "As per Order",
  },
  {
    id: "SLI",
    name: "As per Sellable Item",
  },
  {
    id: "NOS",
    name: "Not Selected",
  },
]

const ConfirmPanel = ({ closePanel }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [dc] = useCurrentDc()
  const [{ search, date }] = useSearchFilter()
  const data = useSelector((state: any) => state.dispatch)
  const orders = useSelectedOrdersArray()
  const { data: summaryAllOrders } = useSummaryAllOrders()

  const handleOnClose = () => {
    setIsOpen(false)
  }

  const showCreateTripDialog = () => {
    if (validate()) setIsOpen(true)
  }

  const validate = () => {
    if (!parsedVariables?.id && !parsedVariables.organisationUserId) {
      AppToaster.show({
        message:
          "Please specify the user under whom the dispatch will be created (Dispatch Under)",
        intent: "danger",
        icon: "cross",
      })
      return false
    }
    return true
  }

  const { ordered: items } = useMemo(() => {
    let totalWeight = 0
    const allSellableItems = []
    orders.forEach(ord => {
      if (ord?.orderItems) {
        allSellableItems.push(...ord?.orderItems?.filter(i => i?.isSelected))
        totalWeight += ord.totalWeight
      }
    })

    const ordered = allSellableItems?.reduce((prev, i) => {
      const item = i?.sellableItem
      const prevQty = prev?.[item?.id]?.quantity
      const quantity = i.newQuantity ?? i.quantity
      return {
        ...prev,
        [item?.id]: {
          id: item?.id,
          name: item?.name,
          rate: i.rate,
          quantity: prevQty ? prevQty + quantity : quantity,
        },
      }
    }, {})

    return { ordered, totalWeight }
  }, [orders])

  const totalUnits =
    items && Object.values(items).reduce((s, i: any) => s + i.quantity, 0)

  const orderItems = orders?.reduce(
    (prev, o, idx) => [
      ...prev,
      ...o.orderItems
        ?.filter(i => i.isSelected)
        ?.map(i => ({
          id: i.bookingId,
          scheduleId: i?.scheduleId,
          orderItemId: i?.id,
          orderId: o?.id,
          quantity: i?.newQuantity ?? i?.quantity,
          sequenceNumber: idx + 1,
        })),
    ],
    []
  )

  //

  const dispatch = useDispatch()
  function handleChange(path, value) {
    dispatch({
      type: CHANGE_DISPATCH_SELECTOR,
      payload: value,
      path,
    })
  }

  const parsedVariables = useMemo(() => {
    const td = data?.transportDetail
    return {
      id: data?.id,
      date: data?.date,
      vehicleId: data?.vehicle?.id,
      vehicleNo: data?.vehicle?.registrationNumber,
      source: data?.sourceDepot?.name,
      destination: data?.destinationDepot?.name,
      labourCount: data?.labourCount,
      aggregatorId: data?.aggregator?.id,
      aggregator: data?.aggregator?.fullName,
      sourceDepotId: data?.sourceDepot?.id,
      destinationDepotId: data?.destinationDepot?.id,
      transportDetail: {
        id: td?.id,
        acceptedDeduction: 0,
        advanceCost: td?.advanceCost || 0,
        transportCost: td?.transportCost || 0,
      },
      organisationUserId: data?.organisationUser?.id,
      estimatedStartTime: data?.estimatedStartTime?.toISOString(),
      bookings: orderItems,
      driver: data?.vehicle?.driver?.fullName,
      driverNumber: data?.vehicle?.driver?.number,
      loadingPattern: data?.loadingPattern?.id,
      estimatedDespatchTime: data?.estimatedDespatchTime,
    }
  }, [data, orderItems])
  const disabled = useFormDisabled("dispatch", parsedVariables)

  return (
    <>
      <VehicleSelector onBackClick={closePanel} />

      <CreateTripDialog
        isOpen={isOpen}
        onClose={handleOnClose}
        disabled={disabled}
        dc={dc}
        search={search}
        date={date}
        data={parsedVariables}
        orders={orders}
        orderItems={values(items)}
        totalUnits={totalUnits}
        summaryAllOrders={summaryAllOrders.summaryAllOrders}
      />
      <FormController source={data} onChange={handleChange}>
        <section className="vt-panel p-3">
          <CustomFormGroup fill label="Date">
            <CustomDatePicker fill path="date" />
          </CustomFormGroup>
          <CustomFormGroup label="Estimated Start Time">
            <CustomDatePicker
              fill
              path="estimatedStartTime"
              format="MMMM Do YYYY, h:mm:ss A"
              label="Broadcast Time"
              valueAs="object"
              popoverProps={{
                minimal: true,
                position: Position.BOTTOM_RIGHT,
                popoverClassName: "vt-confirm-date-picker-popover",
              }}
              closeOnSelect={false}
              timePickerProps={{
                useAmPm: true,
                showArrowButtons: true,
                precision: TimePrecision.SECOND,
              }}
            />
          </CustomFormGroup>

          <DynamicSelector
            label="Delivery Agent"
            path="aggregator"
            searchVector={["fullName"]}
            name={["fullName"]}
            query={USERS}
            variables={{
              group: "Aggregator",
            }}
          />
          <DynamicSelector
            searchVector={["fullName"]}
            name={["fullName"]}
            path="organisationUser"
            label="Dispatch Under"
            query={USERS}
            variables={{
              group: "Organisation",
            }}
          />

          <StringInput
            label="Labour Count"
            type="number"
            path="labourCount"
            big={false}
          />
          <p className="mb-0">Transport Conditions</p>
          <hr className="mt-0" />
          <StringInput
            className="vt-confirm-number-input"
            label="Transport Cost"
            leftIcon="rupee"
            big={false}
            path="transportDetail.transportCost"
          />
          <StringInput
            className="vt-confirm-number-input"
            label="Advance Cost"
            leftIcon="rupee"
            big={false}
            path="transportDetail.advanceCost"
          />
          <p className="mb-0">Vehicle loading</p>
          <hr className="mt-0" />

          <CustomFormGroup label="Estimated Dispatch Time">
            <CustomDatePicker
              fill
              path="estimatedDespatchTime"
              format="MMMM Do YYYY, h:mm:ss A"
              label="Estimated Dispatch Time"
              valueAs="object"
              popoverProps={{
                minimal: true,
                position: Position.BOTTOM_RIGHT,
                popoverClassName: "vt-confirm-date-picker-popover",
              }}
              closeOnSelect={false}
              timePickerProps={{
                useAmPm: true,
                showArrowButtons: true,
                precision: TimePrecision.SECOND,
              }}
            />
          </CustomFormGroup>

          <StaticSelector
            path="loadingPattern"
            label="Loading pattern"
            items={LOADING_PATTERNS}
            value={
              LOADING_PATTERNS.filter(a => a.id === data?.loadingPattern)[0] ||
              LOADING_PATTERNS[0]
            }
          />
        </section>
      </FormController>

      <div className="p-3 vt-dispatch-panel-button">
        <CustomButton
          fill
          big
          dark
          text="Create Trip"
          icon="endorsed"
          onClick={() => showCreateTripDialog()}
        />
      </div>
    </>
  )
}

export default ConfirmPanel
